import React, { SetStateAction, useEffect, useState } from "react";
import { ITableSettingsLegacy } from "../../../constants/Table/interfaces";

interface ITableFooterProps {
    tableSettings: ITableSettingsLegacy;
    setTableSettings: (data: SetStateAction<ITableSettingsLegacy>) => void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    totalProducts: number;
    tableSettingsLoaded: boolean;
    selectedCount?: number;
    leftSlot?: React.ReactNode;
}

const LIMIT_OPTIONS = [20, 100, 500, 1000];

const TableFooter: React.FC<ITableFooterProps> = React.memo(({
    tableSettings,
    setTableSettings,
    pageSize,
    setPageSize,
    totalProducts,
    tableSettingsLoaded,
    selectedCount = 0,
    leftSlot,
}) => {
    const currentPage = tableSettings.page;
    const pageCount = pageSize > 0 ? Math.ceil(totalProducts / pageSize) : 0;
    const [goToPage, setGoToPage] = useState<string>(String(currentPage));

    // Текущее значение limit всегда присутствует в списке (авто-значение при первой загрузке или выбор пользователя)
    const limitOptions = Array.from(new Set([...LIMIT_OPTIONS, tableSettings.limit])).sort((a, b) => a - b);

    useEffect(() => {
        setGoToPage(String(currentPage));
    }, [currentPage]);

    const goToPageNum = (p: number) => {
        const clamped = Math.max(1, Math.min(pageCount, p));
        setTableSettings(prev => ({ ...prev, page: clamped }));
        setGoToPage(String(clamped));
    };

    const handleGoTo = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const p = parseInt((e.target as HTMLInputElement).value);
            if (!isNaN(p)) goToPageNum(p);
        }
    };

    const applyLimit = (v: number) => {
        if (isNaN(v) || v < 1) return;
        setPageSize(v);
        setTableSettings(prev => ({ ...prev, limit: v, page: 1 }));
    };

    // Build compact page list: [1, 2, 3, ..., last-2, last-1, last] with ellipsis
    const buildPages = (): (number | '...')[] => {
        if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
        const pages: (number | '...')[] = [1, 2, 3];
        if (currentPage > 5) pages.push('...');
        if (currentPage > 4 && currentPage < pageCount - 3) pages.push(currentPage);
        if (currentPage < pageCount - 4) pages.push('...');
        pages.push(pageCount - 2, pageCount - 1, pageCount);
        return Array.from(new Set(pages));
    };

    const btnBase = 'min-w-[22px] h-[22px] px-[3px] flex items-center justify-center text-[11px] rounded-[3px] border border-transparent transition-colors';
    const btnDefault = `${btnBase} text-slate-600 hover:bg-blue-50 hover:border-blue-200 dark:text-slate-300 dark:hover:bg-blue-900/30`;
    const btnActive = `${btnBase} bg-c-blue text-white border-c-blue`;
    const btnNav = `${btnBase} text-slate-500 hover:text-c-blue dark:text-slate-400 border-slate-200 dark:border-gray-600`;

    return (
        <div className="bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-700 flex items-center h-[34px] px-2 gap-3 text-[12px] text-slate-600 dark:text-slate-300 select-none mt-0">

            {/* Left slot: С отмеченными / custom actions */}
            {leftSlot && (
                <div className="flex items-center gap-2 flex-shrink-0">
                    {leftSlot}
                </div>
            )}
            {leftSlot && <div className="w-px h-4 bg-slate-200 dark:bg-gray-600 flex-shrink-0" />}

            {/* Left-center: Показать N из TOTAL */}
            <div className="flex items-center gap-1 flex-1 min-w-0">
                <span className="hidden sm:inline text-slate-500 dark:text-slate-400 shrink-0">Показать</span>

                {/* Выпадашка с предустановленными значениями */}
                <div className="relative shrink-0">
                    <select
                        value={tableSettings.limit}
                        onChange={e => applyLimit(parseInt(e.target.value))}
                        className="h-[22px] py-0 pl-[5px] pr-[18px] text-[11px] border border-slate-200 dark:border-gray-600 rounded-[3px] bg-white dark:bg-gray-800 bg-none text-slate-600 dark:text-slate-300 outline-none focus:border-c-blue cursor-pointer appearance-none"
                    >
                        {limitOptions.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <svg className="pointer-events-none absolute right-[4px] top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" width="8" height="5" viewBox="0 0 8 5" fill="none">
                        <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                <span className="hidden sm:inline text-slate-500 dark:text-slate-400 shrink-0">из</span>
                <span className="font-medium shrink-0">{totalProducts}</span>
            </div>

            {/* Right: page navigation */}
            {tableSettingsLoaded && pageCount > 1 && (
                <div className="flex items-center gap-[2px] flex-shrink-0">
                    <button
                        className={btnNav}
                        onClick={() => goToPageNum(currentPage - 1)}
                        disabled={currentPage <= 1}
                    >
                        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <path d="M5 1L1 5l4 4"/>
                        </svg>
                    </button>

                    {/* Page buttons hidden on mobile — only prev/next shown */}
                    <div className="hidden sm:flex items-center gap-[2px]">
                        {buildPages().map((p, i) =>
                            p === '...' ? (
                                <span key={`e${i}`} className="px-1 text-slate-400 text-[11px]">…</span>
                            ) : (
                                <button
                                    key={p}
                                    className={p === currentPage ? btnActive : btnDefault}
                                    onClick={() => goToPageNum(p as number)}
                                >
                                    {p}
                                </button>
                            )
                        )}
                    </div>

                    {/* Mobile: compact X/Y indicator */}
                    <span className="sm:hidden text-[11px] px-1 text-slate-500 dark:text-slate-400">
                        {currentPage}/{pageCount}
                    </span>

                    <button
                        className={btnNav}
                        onClick={() => goToPageNum(currentPage + 1)}
                        disabled={currentPage >= pageCount}
                    >
                        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <path d="M1 1l4 4-4 4"/>
                        </svg>
                    </button>

                    {/* "Перейти на страницу" hidden on mobile per ТЗ */}
                    <span className="hidden sm:inline ml-1 text-slate-500 dark:text-slate-400">Перейти на</span>
                    <input
                        type="number"
                        min={1}
                        max={pageCount}
                        value={goToPage}
                        onChange={e => setGoToPage(e.target.value)}
                        onKeyDown={handleGoTo}
                        onBlur={e => {
                            const p = parseInt(e.target.value);
                            if (!isNaN(p)) goToPageNum(p);
                        }}
                        className="hidden sm:block w-[36px] h-[22px] py-0 px-[2px] text-[11px] text-center border border-slate-200 dark:border-gray-600 rounded-[3px] bg-white dark:bg-gray-800 text-slate-600 dark:text-slate-300 outline-none focus:border-c-blue [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="hidden sm:inline text-slate-500 dark:text-slate-400">страницу</span>
                </div>
            )}
        </div>
    );
});

export default TableFooter;
